// Reconciliation helpers for Bank Statement + Profit & Profit Sharing (Priority).
//
// These numbers are INFORMATIONAL ONLY. Profit already takes payroll out
// through the bank statement lines (money out), so Tech Daily Pay is shown
// next to it to reconcile, never subtracted a second time. Paid invoices are
// never added to money in either: money in is the recorded payments on
// Deposits (the Approved Invoices "Paid" button creates those deposits).

import { entryIsPersonal } from "./profitCalc";

const num = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round(num(n) * 100) / 100;

const dateKeyOf = (value) => {
  if (!value) return "";
  if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "object" && value.seconds) return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  return String(value).slice(0, 10);
};

/** "Payroll" / "Payroll / Wages" (not "Payroll Taxes"). */
export const isPayrollCategoryName = (name = "") => /payroll|wages/i.test(String(name)) && !/tax/i.test(String(name));

const NOT_COUNTED_ACTIONS = new Set([
  "credit_card_payment_transfer",
  "internal_transfer_do_not_count",
  "bank_correction_do_not_count",
  "owner_contribution_do_not_count",
  "matched_existing_do_not_count",
  "owner_draw_personal_do_not_count",
]);

const entryCategoryName = (e = {}) => {
  const c = e.category || "Other";
  const other = String(e.categoryOther || "").trim();
  return c === "Other" && other ? other : c;
};

/** Is this saved bank line a business payroll payment? */
export const isBankPayrollLine = (e = {}) =>
  e.type !== "moneyIn" &&
  !entryIsPersonal(e) &&
  !NOT_COUNTED_ACTIONS.has(e.accountingAction) &&
  isPayrollCategoryName(entryCategoryName(e));

/** { "YYYY-MM": $ } of payroll lines on uploaded / entered bank statements. */
export const bankPayrollByMonth = (entries = []) => {
  const out = {};
  entries.forEach((e) => {
    if (!isBankPayrollLine(e)) return;
    const m = dateKeyOf(e.date).slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(m)) return;
    out[m] = round2((out[m] || 0) + num(e.amount));
  });
  return out;
};

/** Payroll in a computeProfit() result (uploaded lines + typed Monthly Summaries). */
export const payrollFromCategoryTotals = (categoryTotals = []) =>
  round2(
    categoryTotals
      .filter((c) => (c.kind === "business" || !c.kind) && isPayrollCategoryName(c.category))
      .reduce((s, c) => s + num(c.amount), 0)
  );

/** First and last day of a list of "YYYY-MM" months. */
export const monthsRange = (months = []) => {
  const sorted = [...months].filter(Boolean).sort();
  if (!sorted.length) return { startKey: "", endKey: "" };
  const last = sorted[sorted.length - 1];
  const [y, m] = last.split("-").map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { startKey: `${sorted[0]}-01`, endKey: `${last}-${String(lastDay).padStart(2, "0")}` };
};

/* ---------------------- paid invoices vs deposits ---------------------- */

/** Same invoice key Priority's Approved Invoices uses to link deposits. */
export const profitInvoiceKey = (inv = {}) =>
  inv.wo ? String(inv.wo).trim() : String(inv.invoiceNumber || inv.id || "").trim();

const invoiceIsPaid = (inv = {}) =>
  inv.paid === true || String(inv.status || "").trim().toLowerCase() === "paid";

const invoiceAmount = (inv = {}) => num(inv.amount || inv.price || inv.rate || inv.invoiceAmount || inv.total);

const invoicePaidDate = (inv = {}) =>
  dateKeyOf(inv.paidDate || inv.paidAt || inv.invoiceDate || inv.dateOfService || inv.serviceDate);

/**
 * Paid invoices (paid in the given months) that have NO matching deposit.
 * They are not in money in — this list just tells the user a payment was
 * never recorded. Invoices WITH a deposit are already counted through that
 * deposit, so they are never added again.
 */
export const paidInvoicesWithoutDeposit = (invoices = [], deposits = [], months = []) => {
  const monthSet = new Set(months);
  const depositKeys = new Set();
  deposits.forEach((d) => {
    [d.invoiceNumber, d.wo, d.workOrder, d.invoiceId, d.approvedInvoiceId]
      .map((v) => String(v || "").trim())
      .filter(Boolean)
      .forEach((k) => depositKeys.add(k));
    String(d.invoiceNumber2 || "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
      .forEach((k) => depositKeys.add(k));
  });
  const list = invoices.filter((inv) => {
    if (!invoiceIsPaid(inv)) return false;
    if (monthSet.size && !monthSet.has(invoicePaidDate(inv).slice(0, 7))) return false;
    const keys = [profitInvoiceKey(inv), String(inv.invoiceNumber || "").trim(), inv.id].filter(Boolean);
    return !keys.some((k) => depositKeys.has(k));
  });
  return { list, total: round2(list.reduce((s, inv) => s + invoiceAmount(inv), 0)) };
};
